import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import ReactFlow, { Background, Controls, Edge, MiniMap, Node, ReactFlowProvider, useReactFlow } from 'reactflow'
import 'reactflow/dist/style.css'
import { toastService } from '../../services/toast.service'
import { workflowService } from '../../services/workflow.service'
import { Workflow, WorkflowAction, WorkflowActionType, WorkflowTrigger, WorkflowTriggerType } from '../../types'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { ActionEditDialog } from './ActionEditDialog'
import { TriggerEditDialog } from './TriggerEditDialog'
import './WorkflowVisualizationDialog.scss'

interface WorkflowVisualizationDialogProps {
  workflow: Workflow
  onClose: () => void
}

const nodeWidth = 200
const nodeHeight = 100
const horizontalSpacing = 250
const verticalSpacing = 150

const WorkflowFlowContent: React.FC<{
  isFullscreen: boolean
}> = ({ isFullscreen }) => {
  const { fitView } = useReactFlow()

  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 300 })
    }, 100)
    return () => clearTimeout(timer)
  }, [isFullscreen, fitView])

  return (
    <>
      <Background />
      <Controls />
      <MiniMap />
    </>
  )
}

export const WorkflowVisualizationDialog: React.FC<WorkflowVisualizationDialogProps> = ({ workflow: initialWorkflow, onClose }) => {
  const { t } = useTranslation()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showTriggerDialog, setShowTriggerDialog] = useState(false)
  const [showActionDialog, setShowActionDialog] = useState(false)
  const [selectedTrigger, setSelectedTrigger] = useState<WorkflowTrigger | null>(null)
  const [selectedAction, setSelectedAction] = useState<WorkflowAction | null>(null)
  const [selectedTriggerIndex, setSelectedTriggerIndex] = useState<number>(-1)
  const [selectedActionIndex, setSelectedActionIndex] = useState<number>(-1)
  const [workflow, setWorkflow] = useState<Workflow>(initialWorkflow)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [confirmMessage, setConfirmMessage] = useState<string>('')
  const [isAddingTrigger, setIsAddingTrigger] = useState(false)
  const [isAddingAction, setIsAddingAction] = useState(false)

  useEffect(() => {
    setWorkflow(initialWorkflow)
  }, [initialWorkflow])

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const handleNodeClick = (event: React.MouseEvent, node: Node) => {
    if (event.ctrlKey || event.metaKey) {
      if (node.id.startsWith('trigger-')) {
        const triggerIndex = parseInt(node.id.replace('trigger-', ''))
        if (!isNaN(triggerIndex)) {
          handleDeleteTrigger(triggerIndex)
        }
      } else if (node.id.startsWith('action-')) {
        const actionIndex = parseInt(node.id.replace('action-', ''))
        if (!isNaN(actionIndex)) {
          handleDeleteAction(actionIndex)
        }
      }
      return
    }

    if (node.id.startsWith('trigger-')) {
      const triggerIndex = parseInt(node.id.replace('trigger-', ''))
      if (!isNaN(triggerIndex) && workflow.triggers && workflow.triggers[triggerIndex]) {
        setSelectedTrigger(workflow.triggers[triggerIndex])
        setSelectedTriggerIndex(triggerIndex)
        setIsAddingTrigger(false)
        setShowTriggerDialog(true)
      }
    } else if (node.id.startsWith('action-')) {
      const actionIndex = parseInt(node.id.replace('action-', ''))
      if (!isNaN(actionIndex) && workflow.actions && workflow.actions[actionIndex]) {
        setSelectedAction(workflow.actions[actionIndex])
        setSelectedActionIndex(actionIndex)
        setIsAddingAction(false)
        setShowActionDialog(true)
      }
    }
  }

  const handleTriggerSuccess = async (updatedTrigger: WorkflowTrigger) => {
    if (!workflow.id) return

    try {
      let updatedTriggers: WorkflowTrigger[]

      if (isAddingTrigger) {
        updatedTriggers = [...(workflow.triggers || []), updatedTrigger]
      } else if (selectedTriggerIndex >= 0) {
        updatedTriggers = [...(workflow.triggers || [])]
        updatedTriggers[selectedTriggerIndex] = updatedTrigger
      } else {
        updatedTriggers = workflow.triggers || []
      }

      const updatedWorkflow = await workflowService.updateWorkflow(workflow.id, {
        ...workflow,
        triggers: updatedTriggers,
      })
      setWorkflow(updatedWorkflow)
      toastService.showSuccess(t('successfully_updated', { name: workflow.name }))
    } catch (error) {
      toastService.showError(t('error_saving_workflow'))
    }

    setShowTriggerDialog(false)
    setSelectedTrigger(null)
    setSelectedTriggerIndex(-1)
    setIsAddingTrigger(false)
  }

  const handleActionSuccess = async (updatedAction: WorkflowAction) => {
    if (!workflow.id) return

    try {
      const cleanedAction: any = { ...updatedAction }

      if (cleanedAction.type !== WorkflowActionType.Webhook) {
        cleanedAction.webhook = null
      }
      if (cleanedAction.type !== WorkflowActionType.Email) {
        cleanedAction.email = null
      }
      if (cleanedAction.type === WorkflowActionType.Assignment) {
        cleanedAction.assign_workflow_status = cleanedAction.assign_workflow_status ?? null
      }

      let updatedActions: WorkflowAction[]

      if (isAddingAction) {
        updatedActions = [...(workflow.actions || []), cleanedAction]
      } else if (selectedActionIndex >= 0) {
        updatedActions = [...(workflow.actions || [])]
        updatedActions[selectedActionIndex] = cleanedAction
      } else {
        updatedActions = workflow.actions || []
      }

      const updatedWorkflow = await workflowService.updateWorkflow(workflow.id, {
        ...workflow,
        actions: updatedActions,
      })
      setWorkflow(updatedWorkflow)
      toastService.showSuccess(t('successfully_updated', { name: workflow.name }))
    } catch (error) {
      toastService.showError(t('error_saving_workflow'))
    }

    setShowActionDialog(false)
    setSelectedAction(null)
    setSelectedActionIndex(-1)
    setIsAddingAction(false)
  }

  const handleAddTrigger = () => {
    setSelectedTrigger(null)
    setSelectedTriggerIndex(-1)
    setIsAddingTrigger(true)
    setShowTriggerDialog(true)
  }

  const handleAddAction = () => {
    setSelectedAction(null)
    setSelectedActionIndex(-1)
    setIsAddingAction(true)
    setShowActionDialog(true)
  }

  const handleDeleteTrigger = (index: number) => {
    const trigger = workflow.triggers?.[index]
    const triggerName = trigger ? getTriggerDisplayName(trigger.type) : ''

    setConfirmMessage(t('confirm_delete_trigger', { name: triggerName }) || `Da li zaista želite da obrišete okidač "${triggerName}"?`)
    setConfirmAction(() => async () => {
      if (!workflow.id) return

      try {
        const updatedTriggers = workflow.triggers?.filter((_, i) => i !== index) || []
        const updatedWorkflow = await workflowService.updateWorkflow(workflow.id, {
          ...workflow,
          triggers: updatedTriggers,
        })
        setWorkflow(updatedWorkflow)
        toastService.showSuccess(t('successfully_updated', { name: workflow.name }))
        setShowTriggerDialog(false)
        setSelectedTrigger(null)
        setSelectedTriggerIndex(-1)
      } catch (error) {
        toastService.showError(t('error_saving_workflow'))
      }

      setShowConfirmDialog(false)
    })
    setShowConfirmDialog(true)
  }

  const handleDeleteAction = (index: number) => {
    const action = workflow.actions?.[index]
    const actionName = action ? getActionDisplayName(action.type) : ''

    setConfirmMessage(t('confirm_delete_action', { name: actionName }) || `Da li zaista želite da obrišete akciju "${actionName}"?`)
    setConfirmAction(() => async () => {
      if (!workflow.id) return

      try {
        const updatedActions = workflow.actions?.filter((_, i) => i !== index) || []
        const updatedWorkflow = await workflowService.updateWorkflow(workflow.id, {
          ...workflow,
          actions: updatedActions,
        })
        setWorkflow(updatedWorkflow)
        toastService.showSuccess(t('successfully_updated', { name: workflow.name }))
        setShowActionDialog(false)
        setSelectedAction(null)
        setSelectedActionIndex(-1)
      } catch (error) {
        toastService.showError(t('error_saving_workflow'))
      }

      setShowConfirmDialog(false)
    })
    setShowConfirmDialog(true)
  }

  const getTriggerDisplayName = (triggerType: number): string => {
    switch (triggerType) {
      case WorkflowTriggerType.Consumption:
        return t('consumption')
      case WorkflowTriggerType.DocumentAdded:
        return t('document_added')
      case WorkflowTriggerType.DocumentUpdated:
        return t('document_updated')
      case WorkflowTriggerType.Scheduled:
        return t('scheduled')
      case WorkflowTriggerType.DocumentWorkflowStatusChanged:
        return t('trigger_type_document_workflow_status_changed')
      default:
        return String(triggerType)
    }
  }

  const getActionDisplayName = (actionType: number): string => {
    switch (actionType) {
      case WorkflowActionType.Assignment:
        return t('assignment')
      case WorkflowActionType.Removal:
        return t('removal')
      case WorkflowActionType.Email:
        return t('email')
      case WorkflowActionType.Webhook:
        return t('webhook')
      case WorkflowActionType.AttributeAndActionPermissions:
        return t('workflow_action_attribute_action_permissions')
      default:
        return String(actionType)
    }
  }

  const getTriggerDetails = (trigger: any): string => {
    const details: string[] = []

    if (trigger.filter_filename) {
      details.push(`${t('filename')}: ${trigger.filter_filename}`)
    }

    if (trigger.filter_path) {
      details.push(`${t('path')}: ${trigger.filter_path}`)
    }

    if (trigger.match) {
      details.push(`${t('match')}: ${trigger.match}`)
    }

    if (trigger.filter_has_all_tags && trigger.filter_has_all_tags.length > 0) {
      details.push(`${t('tags')}: ${trigger.filter_has_all_tags.length}`)
    }

    if (trigger.schedule_offset_days !== undefined) {
      details.push(`${t('offset_days')}: ${trigger.schedule_offset_days}`)
    }

    return details.length > 0 ? details.join(', ') : ''
  }

  const getActionDetails = (action: any): string => {
    const details: string[] = []

    if (action.assign_title) {
      details.push(`${t('title')}: ${action.assign_title.substring(0, 30)}${action.assign_title.length > 30 ? '...' : ''}`)
    }

    if (action.assign_tags && action.assign_tags.length > 0) {
      details.push(`${t('tags')}: ${action.assign_tags.length}`)
    }

    if (action.email) {
      details.push(`${t('to')}: ${action.email.to || ''}`)
    }

    if (action.webhook) {
      details.push(`${t('url')}: ${action.webhook.url ? action.webhook.url.substring(0, 30) + '...' : ''}`)
    }

    return details.length > 0 ? details.join(', ') : ''
  }

  const { nodes, edges } = useMemo(() => {
    const flowNodes: Node[] = []
    const flowEdges: Edge[] = []

    let yPosition = 50
    let nodeIdCounter = 0

    const startNodeId = `start-${nodeIdCounter++}`
    const startNodeX = Math.max(50, (800 - nodeWidth) / 2)
    flowNodes.push({
      id: startNodeId,
      type: 'input',
      position: { x: startNodeX, y: yPosition },
      data: {
        label: (
          <div className="workflow-node-content">
            <div className="workflow-node-title">{t('start')}</div>
            <div className="workflow-node-details">{workflow.name}</div>
          </div>
        ),
      },
      style: {
        background: '#4ade80',
        color: '#fff',
        border: '2px solid #22c55e',
        borderRadius: '8px',
        width: nodeWidth,
        minHeight: nodeHeight,
      },
    })

    yPosition += verticalSpacing

    const triggerNodes: Node[] = []
    if (workflow.triggers && workflow.triggers.length > 0) {
      const totalWidth = (workflow.triggers.length - 1) * horizontalSpacing + nodeWidth
      const triggerStartX = Math.max(50, (800 - totalWidth) / 2)
      const triggerSpacing = horizontalSpacing

      workflow.triggers.forEach((trigger, index) => {
        const triggerNodeId = `trigger-${index}`
        const triggerX = triggerStartX + index * triggerSpacing

        const triggerNode: Node = {
          id: triggerNodeId,
          position: { x: triggerX, y: yPosition },
          data: {
            label: (
              <div className="workflow-node-content">
                <div className="workflow-node-title">
                  <i className="bi bi-play-circle me-1"></i>
                  {getTriggerDisplayName(trigger.type)}
                </div>
                {getTriggerDetails(trigger) && <div className="workflow-node-details">{getTriggerDetails(trigger)}</div>}
              </div>
            ),
          },
          style: {
            background: '#60a5fa',
            color: '#fff',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            width: nodeWidth,
            minHeight: nodeHeight,
            cursor: 'pointer',
          },
        }

        triggerNodes.push(triggerNode)
        flowNodes.push(triggerNode)

        flowEdges.push({
          id: `edge-${startNodeId}-${triggerNodeId}`,
          source: startNodeId,
          target: triggerNodeId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#3b82f6', strokeWidth: 2 },
        })
      })
    } else {
      const noTriggerNodeId = `no-trigger-${nodeIdCounter++}`
      const noTriggerNodeX = Math.max(50, (800 - nodeWidth) / 2)
      flowNodes.push({
        id: noTriggerNodeId,
        position: { x: noTriggerNodeX, y: yPosition },
        data: { label: t('no_triggers') },
        style: {
          background: '#94a3b8',
          color: '#fff',
          border: '2px solid #64748b',
          borderRadius: '8px',
          width: nodeWidth,
          height: nodeHeight,
        },
      })

      flowEdges.push({
        id: `edge-${startNodeId}-${noTriggerNodeId}`,
        source: startNodeId,
        target: noTriggerNodeId,
        type: 'smoothstep',
        style: { stroke: '#64748b', strokeWidth: 2 },
      })

      triggerNodes.push(flowNodes[flowNodes.length - 1])
    }

    yPosition += verticalSpacing

    const actionNodes: Node[] = []
    if (workflow.actions && workflow.actions.length > 0) {
      const totalWidth = (workflow.actions.length - 1) * horizontalSpacing + nodeWidth
      const actionStartX = Math.max(50, (800 - totalWidth) / 2)
      const actionSpacing = horizontalSpacing

      workflow.actions.forEach((action, index) => {
        const actionNodeId = `action-${index}`
        const actionX = actionStartX + index * actionSpacing

        const actionNode: Node = {
          id: actionNodeId,
          position: { x: actionX, y: yPosition },
          data: {
            label: (
              <div className="workflow-node-content">
                <div className="workflow-node-title">
                  <i className="bi bi-gear me-1"></i>
                  {getActionDisplayName(action.type)}
                </div>
                {getActionDetails(action) && <div className="workflow-node-details">{getActionDetails(action)}</div>}
              </div>
            ),
          },
          style: {
            background: '#f59e0b',
            color: '#fff',
            border: '2px solid #d97706',
            borderRadius: '8px',
            width: nodeWidth,
            minHeight: nodeHeight,
            cursor: 'pointer',
          },
        }

        actionNodes.push(actionNode)
        flowNodes.push(actionNode)

        triggerNodes.forEach((triggerNode) => {
          flowEdges.push({
            id: `edge-${triggerNode.id}-${actionNodeId}`,
            source: triggerNode.id,
            target: actionNodeId,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#f59e0b', strokeWidth: 2 },
          })
        })
      })

      yPosition += verticalSpacing
    }

    const endNodeId = `end-${nodeIdCounter++}`
    const endNodeX = Math.max(50, (800 - nodeWidth) / 2)
    flowNodes.push({
      id: endNodeId,
      type: 'output',
      position: { x: endNodeX, y: yPosition },
      data: {
        label: (
          <div className="workflow-node-content workflow-end-node">
            <div className="workflow-node-title">{t('end')}</div>
          </div>
        ),
      },
      style: {
        background: '#ef4444',
        color: '#fff',
        border: '2px solid #dc2626',
        borderRadius: '8px',
        width: nodeWidth,
        height: nodeHeight,
      },
    })

    if (actionNodes.length > 0) {
      actionNodes.forEach((actionNode) => {
        flowEdges.push({
          id: `edge-${actionNode.id}-${endNodeId}`,
          source: actionNode.id,
          target: endNodeId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#ef4444', strokeWidth: 2 },
        })
      })
    } else {
      triggerNodes.forEach((triggerNode) => {
        flowEdges.push({
          id: `edge-${triggerNode.id}-${endNodeId}`,
          source: triggerNode.id,
          target: endNodeId,
          type: 'smoothstep',
          style: { stroke: '#ef4444', strokeWidth: 2 },
        })
      })
    }

    return { nodes: flowNodes, edges: flowEdges }
  }, [workflow, t])

  return (
    <>
      <Modal
        show={true}
        onHide={onClose}
        size={isFullscreen ? undefined : 'xl'}
        backdrop="static"
        scrollable
        centered={!isFullscreen}
        fullscreen={isFullscreen ? true : undefined}
        className={`workflow-visualization-dialog ${isFullscreen ? 'fullscreen' : ''}`}
        backdropClassName="workflow-visualization-dialog-backdrop"
      >
        <Modal.Header>
          <Modal.Title>
            <i className="bi bi-diagram-3 me-2"></i>
            {t('document_workflow.visualization')}: {workflow.name}
          </Modal.Title>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="link"
              size="sm"
              onClick={toggleFullscreen}
              className="p-1"
              title={isFullscreen ? t('exit_fullscreen') || 'Izađi iz punog ekrana' : t('fullscreen') || 'Puni ekran'}
              style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
            </Button>
            <Button
              variant="link"
              size="sm"
              onClick={onClose}
              className="p-1"
              title={t('close') || 'Zatvori'}
              style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: isFullscreen ? 'calc(100vh - 80px)' : '70vh', padding: 0, position: 'relative' }}>
          <div className="workflow-flow-container">
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                nodesDraggable={true}
                nodesConnectable={false}
                elementsSelectable={true}
                onNodeClick={handleNodeClick}
              >
                <WorkflowFlowContent isFullscreen={isFullscreen} />
              </ReactFlow>
            </ReactFlowProvider>
          </div>
          <div className="workflow-flow-actions">
            <Button variant="primary" size="sm" onClick={handleAddTrigger} className="workflow-add-trigger-btn" title={t('add_trigger')}>
              <i className="bi bi-lightning me-2"></i>
              <i className="bi bi-plus-circle me-1"></i>
              {t('triggers')}
            </Button>
            <Button variant="warning" size="sm" onClick={handleAddAction} className="workflow-add-action-btn" title={t('add_action')}>
              <i className="bi bi-gear me-2"></i>
              <i className="bi bi-plus-circle me-1"></i>
              {t('actions')}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {showTriggerDialog && (
        <TriggerEditDialog
          trigger={selectedTrigger || undefined}
          onSuccess={handleTriggerSuccess}
          onError={() => {
            setShowTriggerDialog(false)
            setSelectedTrigger(null)
            setSelectedTriggerIndex(-1)
            setIsAddingTrigger(false)
          }}
          onClose={() => {
            setShowTriggerDialog(false)
            setSelectedTrigger(null)
            setSelectedTriggerIndex(-1)
            setIsAddingTrigger(false)
          }}
          onDelete={selectedTrigger && selectedTriggerIndex >= 0 ? () => handleDeleteTrigger(selectedTriggerIndex) : undefined}
        />
      )}

      {showActionDialog && (
        <ActionEditDialog
          action={selectedAction || undefined}
          onSuccess={handleActionSuccess}
          onError={() => {
            setShowActionDialog(false)
            setSelectedAction(null)
            setSelectedActionIndex(-1)
            setIsAddingAction(false)
          }}
          onClose={() => {
            setShowActionDialog(false)
            setSelectedAction(null)
            setSelectedActionIndex(-1)
            setIsAddingAction(false)
          }}
          onDelete={selectedAction && selectedActionIndex >= 0 ? () => handleDeleteAction(selectedActionIndex) : undefined}
        />
      )}

      <ConfirmDialog
        show={showConfirmDialog}
        title={t('confirm_delete')}
        message={confirmMessage}
        confirmText={t('delete')}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction()
          }
        }}
        onCancel={() => {
          setShowConfirmDialog(false)
          setConfirmAction(null)
          setConfirmMessage('')
        }}
        variant="danger"
      />
    </>
  )
}
